/**
 * State fact bank — 1,500 facts (30 per state) from the bundled dataset
 * (PoliPoker_US_State_Facts, © source-linked editorial draft).
 *
 * Behaviour (per the dataset brief):
 *  - Each state's 30 fact IDs are shuffled into a no-replacement queue; we draw
 *    until all 30 are shown, then reshuffle — never repeating a fact across a
 *    reshuffle boundary, and biasing successive facts toward different
 *    categories. The first entry (usually basic state info) is not privileged.
 *  - Seen fact IDs persist in localStorage so play resumes mid-cycle.
 *  - Source references resolve locally; the URL is a reference only — facts show
 *    even when the reference site is unavailable.
 *
 * Multiplayer note: the server should pick and broadcast the AWARDED fact id so
 * every seat sees the same fact; optional per-player browsing stays local. Here
 * (single-player) both use the local queue.
 */
import DATA from "./state-facts.json";

interface RawFact { id: string; category: string; text: string; source_id: string; }
export interface Source { title: string; url: string; type?: string; }
export interface ResolvedFact { id: string; category: string; text: string; source: Source; }

const D = DATA as unknown as {
  version?: string;
  sources: Record<string, Source>;
  states: { name: string; code: string; facts: RawFact[] }[];
};

export const DATASET_VERSION = D.version ?? "1.0";
const SOURCES = D.sources;
const BY_STATE = new Map<string, RawFact[]>();
for (const s of D.states) BY_STATE.set(s.code, s.facts);

export function resolveSource(sourceId: string): Source {
  return SOURCES[sourceId] ?? { title: "Source", url: "#" };
}
function resolve(f: RawFact): ResolvedFact {
  return { id: f.id, category: f.category, text: f.text, source: resolveSource(f.source_id) };
}

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Shuffle `ids`, then greedily reorder so consecutive facts differ in category
 * where possible. `avoidId` (the last fact shown) is kept off the front to
 * prevent an immediate repeat across a reshuffle.
 */
function orderWithVariation(code: string, ids: string[], avoidId?: string): string[] {
  const facts = BY_STATE.get(code) ?? [];
  const byId = new Map(facts.map((f) => [f.id, f]));
  const pool = shuffle([...ids]);
  const out: string[] = [];
  let lastCat = avoidId ? byId.get(avoidId)?.category : undefined;
  while (pool.length) {
    let idx = pool.findIndex((id) => byId.get(id)!.category !== lastCat);
    if (idx < 0) idx = 0;
    const [id] = pool.splice(idx, 1);
    out.push(id);
    lastCat = byId.get(id)!.category;
  }
  if (avoidId && out[0] === avoidId && out.length > 1) [out[0], out[1]] = [out[1], out[0]];
  return out;
}

const SEEN_KEY = (code: string) => `poli-facts-seen-${code}`;
function loadSeen(code: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY(code)) || "[]")); } catch { return new Set(); }
}
function saveSeen(code: string, seen: Set<string>): void {
  try { localStorage.setItem(SEEN_KEY(code), JSON.stringify([...seen])); } catch { /* storage unavailable */ }
}

interface QueueState { queue: string[]; last?: string; seen: Set<string>; }
const QUEUES = new Map<string, QueueState>();

function getQueue(code: string): QueueState {
  let q = QUEUES.get(code);
  if (q) return q;
  const facts = BY_STATE.get(code) ?? [];
  const all = facts.map((f) => f.id);
  const seen = loadSeen(code);
  const remaining = all.filter((id) => !seen.has(id));
  q = remaining.length
    ? { queue: orderWithVariation(code, remaining), last: undefined, seen }
    : { queue: orderWithVariation(code, all), last: undefined, seen: new Set() };
  if (!remaining.length) saveSeen(code, q.seen);
  QUEUES.set(code, q);
  return q;
}

/** Draw the next fact for a state (no-replacement; reshuffles after 30). */
export function nextFact(code: string): ResolvedFact | null {
  const facts = BY_STATE.get(code);
  if (!facts || !facts.length) return null;
  const q = getQueue(code);
  if (!q.queue.length) {
    // Cycle complete — reshuffle, clearing seen, avoiding an immediate repeat.
    q.seen = new Set();
    q.queue = orderWithVariation(code, facts.map((f) => f.id), q.last);
    saveSeen(code, q.seen);
  }
  const id = q.queue.shift()!;
  q.last = id;
  q.seen.add(id);
  saveSeen(code, q.seen);
  const f = facts.find((x) => x.id === id)!;
  return resolve(f);
}

export function factCount(code: string): number {
  return BY_STATE.get(code)?.length ?? 0;
}
